type VideoType = 'youtube' | 'vimeo' | 'mp4' | '';

function detectVideoType(url: string): VideoType {
    if (!url) return '';
    if (/(?:youtube\.com|youtu\.be)/i.test(url)) return 'youtube';
    if (/vimeo\.com/i.test(url)) return 'vimeo';
    if (/\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(url)) return 'mp4';
    return '';
}

function getYoutubeId(url: string): string | null {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
    return m?.[1] ?? null;
}

function getVimeoData(url: string): { id: string; hash: string | null } | null {
    // Matches:
    //   https://vimeo.com/123456789
    //   https://vimeo.com/123456789/abc123def         (unlisted privacy hash)
    //   https://vimeo.com/video/123456789
    //   https://vimeo.com/video/123456789/abc123def
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([\w-]+))?/i);
    if (!m) return null;
    return { id: m[1], hash: m[2] ?? null };
}

function buildPlayer(type: VideoType, url: string): HTMLElement | null {
    if (type === 'youtube') {
        const id = getYoutubeId(url);
        if (!id) return null;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.className = 'sbs-calm-gallery__video-player';
        iframe.setAttribute('frameborder', '0');
        return iframe;
    }
    if (type === 'vimeo') {
        const data = getVimeoData(url);
        if (!data) return null;
        const params = new URLSearchParams({ autoplay: '1' });
        if (data.hash) params.set('h', data.hash);
        const iframe = document.createElement('iframe');
        iframe.src = `https://player.vimeo.com/video/${data.id}?${params.toString()}`;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.className = 'sbs-calm-gallery__video-player';
        iframe.setAttribute('frameborder', '0');
        return iframe;
    }
    if (type === 'mp4') {
        const video = document.createElement('video');
        video.src = url;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.className = 'sbs-calm-gallery__video-player';
        return video;
    }
    return null;
}

// ============================================================
// Body scroll lock for the lightbox.
// Replaces the disappearing scrollbar's width with right-padding
// on the body so the page behind the modal doesn't reflow, and
// the modal's centering math no longer competes with scrollbar
// area. Flag prevents double-lock if open/close events desync.
// ============================================================

let bodyLocked = false;

function lockBodyScroll() {
    if (bodyLocked) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';
    bodyLocked = true;
}

function unlockBodyScroll() {
    if (!bodyLocked) return;
    document.body.style.paddingRight = '';
    document.body.style.overflow = '';
    bodyLocked = false;
}

function initVideo() {
    const containers = document.querySelectorAll<HTMLElement>('.sbs-calm-gallery__video[data-video-url]');
    containers.forEach((el) => {
        const url = el.dataset.videoUrl ?? '';
        const declaredType = (el.dataset.videoType as VideoType) || '';
        const type = declaredType || detectVideoType(url);

        const button = el.querySelector<HTMLButtonElement>('.sbs-calm-gallery__play');
        const poster = el.querySelector<HTMLImageElement>('.sbs-calm-gallery__video-poster');

        const handleClick = (e: Event) => {
            e.preventDefault();
            const player = buildPlayer(type, url);
            if (!player) return;
            poster?.remove();
            button?.remove();
            el.appendChild(player);
        };

        button?.addEventListener('click', handleClick);
    });
}

function initLightbox() {
    // One <dialog> per .sbs-calm-gallery block; scope every block's triggers to its own dialog.
    const blocks = document.querySelectorAll<HTMLElement>('.sbs-calm-gallery');

    blocks.forEach((block) => {
        const dialog = block.querySelector<HTMLDialogElement>('.sbs-calm-gallery__lightbox');
        if (!dialog) return;

        const dialogImg = dialog.querySelector<HTMLImageElement>('.sbs-calm-gallery__lightbox-img');
        const closeBtn  = dialog.querySelector<HTMLButtonElement>('.sbs-calm-gallery__lightbox-close');
        if (!dialogImg || !closeBtn) return;

        const triggers = block.querySelectorAll<HTMLButtonElement>('.sbs-calm-gallery__cell-zoom[data-cg-zoom]');

        triggers.forEach((trigger) => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const url = trigger.dataset.cgZoom ?? '';
                const alt = trigger.dataset.cgAlt ?? '';
                if (!url) return;

                dialogImg.src = url;
                dialogImg.alt = alt;

                lockBodyScroll();

                if (typeof dialog.showModal === 'function') {
                    dialog.showModal();
                } else {
                    // Fallback for browsers without <dialog> support — show inline.
                    dialog.setAttribute('open', '');
                }
            });
        });

        // Close button
        closeBtn.addEventListener('click', () => {
            dialog.close();
        });

        // Click outside the image (on the dialog's empty area) closes the lightbox.
        // e.target === dialog only when the click hits the dialog itself, not a child.
        dialog.addEventListener('click', (e) => {
            if (e.target === dialog) {
                dialog.close();
            }
        });

        // Free image memory and restore body scroll when dialog closes.
        // The 'close' event fires whether closure was via .close(), Esc key, or the close button.
        dialog.addEventListener('close', () => {
            dialogImg.src = '';
            dialogImg.alt = '';
            unlockBodyScroll();
        });
    });
}

function init() {
    initVideo();
    initLightbox();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
